import { camelCase } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Product, ProductCollectionItem } from '../../../../types/operations';

export const useProductContent = () => {
  const { t } = useTranslation();

  const productAttributes: Partial<
    Record<
      keyof Product,
      {
        label: string;
        description: string;
      }
    >
  > = useMemo(
    () => ({
      class: {
        label: t('ecommerce:product.class.label', { defaultValue: 'Product class' }),
        description: t('ecommerce:product.class.description', { defaultValue: '' }),
      },
      series: {
        label: t('ecommerce:product.series.label', { defaultValue: 'Product series' }),
        description: t('ecommerce:product.series.description', { defaultValue: '' }),
      },
      family: {
        label: t('ecommerce:product.family.label', { defaultValue: 'Product family' }),
        description: t('ecommerce:product.family.description', { defaultValue: '' }),
      },
      housingType: {
        label: t('ecommerce:product.housingType.label', { defaultValue: 'Housing type' }),
        description: t('ecommerce:product.housingType.description', { defaultValue: '' }),
      },
      portConfiguration: {
        label: t('ecommerce:product.portConfiguration.label', { defaultValue: 'Port configuration' }),
        description: t('ecommerce:product.portConfiguration.description', { defaultValue: '' }),
      },
      sealMaterial: {
        label: t('ecommerce:product.sealMaterial.label', { defaultValue: 'Seal material' }),
        description: t('ecommerce:product.sealMaterial.description', {
          defaultValue: 'The material used for the seals in the valve',
        }),
      },
      sealMaterialORing: {
        label: t('ecommerce:product.sealMaterialORing.label', { defaultValue: 'Seal material O-ring' }),
        description: t('ecommerce:product.sealMaterialORing.description', {
          defaultValue: 'The sealing material used for the valve (O-ring).',
        }),
      },
      operationPressureLabelLaser: {
        label: t('ecommerce:product.operationPressureLabelLaser.label', {
          defaultValue: 'Operation pressure',
        }),
        description: t('ecommerce:product.operationPressureLabelLaser.description', {
          defaultValue: 'The maximum pressure that can be used for valve operation.',
        }),
      },
      sizeLower: {
        label: t('ecommerce:product.sizeLower.label', { defaultValue: 'Pipe size (Lower)' }),
        description: t('ecommerce:product.sizeLower.description', { defaultValue: '' }),
      },
      sizeUpper: {
        label: t('ecommerce:product.sizeUpper.label', { defaultValue: 'Pipe size' }),
        description: t('ecommerce:product.sizeUpper.description', {
          defaultValue: 'The pipe diameter to which the valve will be connected.',
        }),
      },
      materialInsert: {
        label: t('ecommerce:product.materialInsert.label', { defaultValue: 'Material' }),
        description: t('ecommerce:product.materialInsert.description', {
          defaultValue: 'The material the valve is made of.',
        }),
      },
      actuation: {
        label: t('ecommerce:product.actuation.label', { defaultValue: 'Working Mode' }),
        description: t('ecommerce:product.actuation.description', {
          defaultValue: 'The mechanism for opening and closing a valve',
        }),
      },
      apzLb: {
        label: t('ecommerce:product.apzLb.label', { defaultValue: 'Certification' }),
        description: t('ecommerce:product.apzLb.description', {
          defaultValue: 'The certification that the valve must meet',
        }),
      },
      amountOfSolenoids: {
        label: t('ecommerce:product.amountOfSolenoids.label', { defaultValue: 'Amount of solenoids' }),
        description: t('ecommerce:product.amountOfSolenoids.description', {
          defaultValue: 'The amount of solenoids the valve control unit should have for the desired operation.',
        }),
      },
      communicationProtocol: {
        label: t('ecommerce:product.communicationProtocol.label', { defaultValue: 'Communication type' }),
        description: t('ecommerce:product.communicationProtocol.description', { defaultValue: '' }),
      },
      connection: {
        label: t('ecommerce:product.connection.label', { defaultValue: 'Control unit connection type' }),
        description: t('ecommerce:product.connection.description', { defaultValue: '' }),
      },
      materialKey: {
        label: t('ecommerce:product.materialKey.label', { defaultValue: 'Material key' }),
        description: t('ecommerce:product.materialKey.description', { defaultValue: '' }),
      },
      descriptionValveInsertHousing: {
        label: t('ecommerce:product.descriptionValveInsertHousing.label', {
          defaultValue: 'Description valve insert housing',
        }),
        description: t('ecommerce:product.descriptionValveInsertHousing.description', { defaultValue: '' }),
      },
      typeForLaser: {
        label: t('ecommerce:product.typeForLaser.label', { defaultValue: 'Type for Laser' }),
        description: t('ecommerce:product.typeForLaser.description', { defaultValue: '' }),
      },
      skuInactiveStatus: {
        label: t('ecommerce:product.skuInactiveStatus.label', { defaultValue: 'SKU inactive status' }),
        description: t('ecommerce:product.skuInactiveStatus.description', { defaultValue: '' }),
      },
      mountingKitSku: {
        label: t('ecommerce:product.mountingKitSku.label', { defaultValue: 'Mounting kit SKU' }),
        description: t('ecommerce:product.mountingKitSku.description', { defaultValue: '' }),
      },
      mountingKitType: {
        label: t('ecommerce:product.mountingKitType.label', { defaultValue: 'Mounting kit type' }),
        description: t('ecommerce:product.mountingKitType.description', { defaultValue: '' }),
      },
      mountingKitSize: {
        label: t('ecommerce:product.mountingKitSize.label', { defaultValue: 'Mounting kit size' }),
        description: t('ecommerce:product.mountingKitSize.description', { defaultValue: '' }),
      },
      mountingKitFamilyDn25: {
        label: t('ecommerce:product.mountingKitFamilyDn25.label', {
          defaultValue: 'Mounting kit family DN25',
        }),
        description: t('ecommerce:product.mountingKitFamilyDn25.description', { defaultValue: '' }),
      },
      mountingKitFamilyDn125: {
        label: t('ecommerce:product.mountingKitFamilyDn125.label', {
          defaultValue: 'Mounting kit family DN125',
        }),
        description: t('ecommerce:product.mountingKitFamilyDn125.description', { defaultValue: '' }),
      },
      controlUnitSku: {
        label: t('ecommerce:product.controlUnitSku.label', { defaultValue: 'Control Unit SKU' }),
        description: t('ecommerce:product.controlUnitSku.description', { defaultValue: '' }),
      },
      controlUnitClass: {
        label: t('ecommerce:product.controlUnitClass.label', { defaultValue: 'Control Unit Class' }),
        description: t('ecommerce:product.controlUnitClass.description', { defaultValue: '' }),
      },
      controlUnitSeries: {
        label: t('ecommerce:product.controlUnitSeries.label', { defaultValue: 'Control Unit' }),
        description: t('ecommerce:product.controlUnitSeries.description', {
          defaultValue: 'The component used to control the valve.',
        }),
      },
      controlUnitCommunicationProtocol: {
        label: t('ecommerce:product.controlUnitCommunicationProtocol.label', {
          defaultValue: 'Control Unit Communication Protocol',
        }),
        description: t('ecommerce:product.controlUnitCommunicationProtocol.description', { defaultValue: '' }),
      },
      controlUnitConnection: {
        label: t('ecommerce:product.controlUnitConnection.label', { defaultValue: 'Control Unit Connection Type' }),
        description: t('ecommerce:product.controlUnitConnection.description', { defaultValue: '' }),
      },
      id: {
        label: t('ecommerce:product.id.label', { defaultValue: 'ID Number' }),
        description: t('ecommerce:product.id.description', { defaultValue: '' }),
      },
      productType: {
        label: t('ecommerce:product.productType.label', { defaultValue: 'Product type' }),
        description: '',
      },
      typeDSV: { label: t('ecommerce:product.typeDsv.label', { defaultValue: 'Type of DSV' }), description: '' },
      sku: { label: t('ecommerce:product.sku.label', { defaultValue: 'SKU' }), description: '' },
      conform3A: { label: t('ecommerce:product.conform3A.label', { defaultValue: 'Confirm 3A' }), description: '' },
      actuationType: {
        label: t('ecommerce:product.actuationType.label', { defaultValue: 'Actuation type' }),
        description: '',
      },
      actuatorPressureLabelLaser: {
        label: t('ecommerce:product.actuatorPressureLabelLaser.label', {
          defaultValue: 'Control air pressure',
        }),
        description: '',
      },
      additionalMarking: {
        label: t('ecommerce:product.additionalMarking.label', {
          defaultValue: 'Additional marking',
        }),
        description: '',
      },
      atexLb: { label: t('ecommerce:product.atexLb.label', { defaultValue: 'ATEX LB' }), description: '' },
      atex2Lb: { label: t('ecommerce:product.atex2Lb.label', { defaultValue: 'ATEX 2 LB' }), description: '' },
      ceLabeling: { label: t('ecommerce:product.ceLabeling.label', { defaultValue: 'CE labeling' }), description: '' },
      crnLabeling: {
        label: t('ecommerce:product.crnLabeling.label', { defaultValue: 'CRN labeling' }),
        description: '',
      },
      deliveryTimeKey: {
        label: t('ecommerce:product.deliveryTimeKey.label', { defaultValue: 'Delivery time' }),
        description: '',
      },

      entryDate: { label: t('ecommerce:product.entryDate.label', { defaultValue: 'Entry date' }), description: '' },
      gostR: { label: t('ecommerce:product.gostR.label', { defaultValue: 'GOST R' }), description: '' },

      materialNumberInsert: {
        label: t('ecommerce:product.materialNumberInsert.label', {
          defaultValue: 'Material number insert',
        }),
        description: '',
      },

      measurementProjectEMEA: {
        label: t('ecommerce:product.measurementProjectEMEA.label', {
          defaultValue: 'Measurement project EMEA',
        }),
        description: '',
      },

      positionFileActuator: {
        label: t('ecommerce:product.positionFileActuator.label', {
          defaultValue: 'Position file actuator',
        }),
        description: '',
      },
      qualityClass: {
        label: t('ecommerce:product.qualityClass.label', { defaultValue: 'Quality class' }),
        description: '',
      },
      setOfGasketsAs1Label: {
        label: t('ecommerce:product.setOfGasketsAs1Label.label', {
          defaultValue: 'Set of gaskets AS1 label',
        }),
        description: '',
      },
      setOfGasketsPs1Label: {
        label: t('ecommerce:product.setOfGasketsPs1Label.label', {
          defaultValue: 'Set of gaskets PS1 label',
        }),
        description: '',
      },
      surface: { label: t('ecommerce:product.surface.label', { defaultValue: 'Surface finish' }), description: '' },
      tempFileActuator: {
        label: t('ecommerce:product.tempFileActuator.label', { defaultValue: 'Temp file actuator' }),
        description: '',
      },
      windchillNumber1: {
        label: t('ecommerce:product.windchillNumber1.label', { defaultValue: 'Windchill number 1' }),
        description: '',
      },
      suffixMounting: {
        label: t('ecommerce:product.suffixMounting.label', { defaultValue: 'Suffix mounting' }),
        description: '',
      },
      changeNo1: { label: t('ecommerce:product.changeNo1.label', { defaultValue: 'Change no 1' }), description: '' },
      drawingNo1: { label: t('ecommerce:product.drawingNo1.label', { defaultValue: 'Drawing no 1' }), description: '' },
      createdAt: { label: t('ecommerce:product.createdAt.label', { defaultValue: 'Created at' }), description: '' },
      createdBy: { label: t('ecommerce:product.createdBy.label', { defaultValue: 'Created by' }), description: '' },
      updatedAt: { label: t('ecommerce:product.updatedAt.label', { defaultValue: 'Updated at' }), description: '' },
      updatedBy: { label: t('ecommerce:product.updatedBy.label', { defaultValue: 'Updated by' }), description: '' },
    }),
    [t]
  );

  const productDescriptionParts = useMemo(
    () => ({
      isHygienic: t('ecommerce:product.description.isHygienic', { defaultValue: 'This is a hygenic valve.' }),
      isAseptic: t('ecommerce:product.description.isAseptic', { defaultValue: 'This is a aseptic valve.' }),
      isTankBottom: t('ecommerce:product.description.isTankBottom', {
        defaultValue:
          'Typically this valve is used to control process or CIP fluids exiting and entering tanks or vessels.',
      }),
      isMixProof: t('ecommerce:product.description.isMixProof', {
        defaultValue:
          'Typically this valve can be used while simultaneously a specific production zone is in cleaning.',
      }),
      isManual: t('ecommerce:product.description.isManual', {
        defaultValue: 'The method of controlling this valve is manual.',
      }),
      isPneumatic: t('ecommerce:product.description.isPneumatic', {
        defaultValue: 'This valve is pneumatically operated.',
      }),
      isChangeover: t('ecommerce:product.description.isChangeover', {
        defaultValue: 'This changover valve is used to switching flow from one pipeline to another.',
      }),
      isNC: t('ecommerce:product.description.isNC', {
        defaultValue: 'The actuator has the operation mode "Spring to Close" (NC).',
      }),
      isAA: t('ecommerce:product.description.isAA', {
        defaultValue: '"Air to Close" (AA) is the operation mode of this actuator.',
      }),
      isDistributing: t('ecommerce:product.description.isDistributing', {
        defaultValue:
          'Running from one line (1) into either another (2) or a third line (3) is where is valve is designed for.',
      }),
      isMixing: t('ecommerce:product.description.isMixing', {
        defaultValue:
          'Running from ether one line (2) or a second line (3) into the same line (1) is where this valve is designed for.',
      }),
      isSeatliftingCleaning: t('ecommerce:product.description.isSeatliftingCleaning', {
        defaultValue: 'Cleaning this valve can be executed using Seat lifting.',
      }),
      isExternalCleaning: t('ecommerce:product.description.isExternalCleaning', {
        defaultValue: 'Cleaning this valve can be executed using external flushing connections.',
      }),
      isSterilization: t('ecommerce:product.description.isSterilization', {
        defaultValue: 'Addiontal sterillization of the shafts are possible with this valve.',
      }),
      isPMO: t('ecommerce:product.description.isPMO', { defaultValue: 'This valve is PMO compliant.' }),
      isDirectConnection: t('ecommerce:product.description.isDirectConnection', {
        defaultValue: 'Block flange will be used to connect this valve towards for example the tank.',
      }),
      isSplitBody: t('ecommerce:product.description.isSplitBody', {
        defaultValue: 'The valve features a split housing body with a clamp connection.',
      }),
    }),
    [t]
  );

  const getDescriptionPart = useCallback(
    (product: Product | ProductCollectionItem, part: keyof typeof productDescriptionParts) => {
      return product[part] ? productDescriptionParts[part] : '';
    },
    [productDescriptionParts]
  );

  return {
    productDescription: useCallback(
      (product: Product | ProductCollectionItem) => {
        return [
          getDescriptionPart(product, 'isHygienic'),
          getDescriptionPart(product, 'isAseptic'),
          getDescriptionPart(product, 'isTankBottom'),
          getDescriptionPart(product, 'isMixProof'),
          getDescriptionPart(product, 'isManual'),
          getDescriptionPart(product, 'isPneumatic'),
          getDescriptionPart(product, 'isChangeover'),
          getDescriptionPart(product, 'isNC'),
          getDescriptionPart(product, 'isAA'),
          getDescriptionPart(product, 'isDistributing'),
          getDescriptionPart(product, 'isMixing'),
          getDescriptionPart(product, 'isSeatliftingCleaning'),
          getDescriptionPart(product, 'isExternalCleaning'),
          getDescriptionPart(product, 'isSterilization'),
          getDescriptionPart(product, 'isPMO'),
          getDescriptionPart(product, 'isDirectConnection'),
          getDescriptionPart(product, 'isSplitBody'),
        ]
          .filter(Boolean)
          .join(' ');
      },
      [getDescriptionPart]
    ),
    attributeInfo: useCallback(
      (key: keyof Product | string, _value?: any) => {
        let value = _value?.toString();

        return {
          label: key,
          description: '',
          value: value
            ? t(`ecommerce:productAttributesOptions.${camelCase(value)}`, {
                defaultValue: value,
              })
            : undefined,
          ...(productAttributes[key as keyof Product] || {}),
        };
      },
      [productAttributes, t]
    ),
  };
};
